<!--
	@component
	Hover-triggered action menu (kebab trigger) revealing a dropdown of
	`ActionMenuItem`s. When `appendToElement` is set, the dropdown is portaled into
	that element (e.g. `document.body`) so it escapes the `overflow` clipping of
	ancestor containers, positioned relative to the trigger.
-->
<script lang="ts">
	import { fade } from 'svelte/transition'
	import type { Attachment } from 'svelte/attachments'
	import type { Snippet } from 'svelte'

	interface Props {
		minWidth?: string
		right?: string
		top?: string
		appendToElement?: HTMLElement | null
		titleAttr?: string
		// custom trigger; falls back to the default `.action-btn` when omitted
		trigger?: Snippet
		// menu content, expected to hold ActionMenuItem elements for correct styling
		content?: Snippet
	}

	let {
		minWidth = '180px',
		right = '0',
		top = '0.75rem',
		appendToElement = null,
		titleAttr = '',
		trigger,
		content
	}: Props = $props()

	let actionMenuContainer: HTMLElement | null = $state(null)

	let active: boolean = $state(false)

	function toggleMenu() {
		active = !active
	}

	/**
	 * portal attachment: moves the dropdown into `appendToElement`, positioned
	 * relative to the trigger. the teardown restores the node to its original
	 * slot so svelte can unmount it cleanly.
	 */
	const portalMenu: Attachment<HTMLElement> = (node) => {
		if (!active || !appendToElement || !actionMenuContainer) return

		const appendToRect = appendToElement.getBoundingClientRect()
		const targetRect = actionMenuContainer.getBoundingClientRect()

		node.style.top = `${targetRect.top - appendToRect.top - actionMenuContainer.offsetHeight}px`
		node.style.left = `${targetRect.left - appendToRect.left}px`
		// extend the item over the action-btn so the mouseleave event still fires
		node.style.paddingTop = `${actionMenuContainer.offsetHeight * 2}px`

		const originalParent = node.parentNode
		const originalNextSibling = node.nextSibling
		appendToElement.appendChild(node)

		return () => {
			originalParent?.insertBefore(node, originalNextSibling)
			node.style.top = ''
			node.style.left = ''
			node.style.paddingTop = ''
		}
	}
</script>

<!-- We only toggle the mouse leave here, if the menu item is not appended somewhere else. Otherwise, hovering over the menu item is not possible -->
<div
	bind:this={actionMenuContainer}
	class="action-menu-container"
	class:active
	style="--min-width: {minWidth}; --right:{right}"
	onmouseenter={toggleMenu}
	onmouseleave={() => {
		if (!appendToElement) toggleMenu()
	}}
>
	{#if trigger}
		{@render trigger()}
	{:else}
		<div class="action-btn" title={titleAttr}></div>
	{/if}
	<!-- We toggle the mouse leave here, if the menu item is appended somewhere else to allow hovering correctly -->
	<div
		class="action-menu-item"
		style="position: absolute;"
		style:display={active ? 'block' : 'none'}
		{@attach portalMenu}
		onmouseleave={() => {
			if (appendToElement) toggleMenu()
		}}
	>
		<ul transition:fade={{ duration: 300 }} style="--top: {top}">
			<!--always use the ActionMenuItem to use the correct style-->
			{@render content?.()}
		</ul>
	</div>
</div>

<style>
	@import '$styles/global.scss';

	.action-menu-container {
		cursor: pointer;
	}

	.action-menu-item {
		cursor: pointer;

		&::before {
			content: '';
			position: absolute;
			top: -10px;
			right: var(--right);
			width: var(--min-width);
			height: 14px;
			z-index: 11;
			clip-path: polygon(var(--right) 0, calc(var(--right) + 20px) 0, 100% 100%, 0 100%);
		}

		& ul {
			position: relative;
			top: 0.25rem;
			right: var(--right);
			background: white;
			min-width: var(--min-width);
			padding: 0.25rem 0.75rem;
			border-radius: 4px;
			transition: 0.3s;
			border: 1px solid #ddd;
			box-shadow: 0 0 5px rgba(0, 0, 0, 0.25);
			z-index: 11;
		}

		&.active ul {
			top: var(--top);
			transition: 0.3s;
		}
	}

	.action-menu-container :global(li.action-item:not(:last-of-type)) {
		border-bottom: 1px solid rgba(0, 0, 0, 0.1);
	}

	.action-btn {
		background: url('@/icons/icon_3dot_menu.svg') no-repeat;
		width: 20px;
		height: 20px;
		background-size: contain;
	}
</style>
